'use client'

import { useCallback, useRef, useState } from 'react'

/**
 * Hook that provides throttled state updates
 * Updates are batched and only applied at most once per throttle interval
 *
 * @param initialValue - Initial state value
 * @param throttleMs - Throttle interval in milliseconds (default: 100ms)
 */
export function useThrottledState<T>(initialValue: T, throttleMs: number = 100) {
  const [state, setState] = useState<T>(initialValue)
  const valueRef = useRef<T>(initialValue)
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const throttledSetState = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      // Update ref immediately (no re-render)
      if (typeof newValue === 'function') {
        valueRef.current = (newValue as (prev: T) => T)(valueRef.current)
      } else {
        valueRef.current = newValue
      }

      // Throttle actual state update
      if (throttleRef.current) return

      throttleRef.current = setTimeout(() => {
        setState(valueRef.current)
        throttleRef.current = null
      }, throttleMs)
    },
    [throttleMs]
  )

  // Force immediate update (bypass throttle)
  const forceUpdate = useCallback(() => {
    if (throttleRef.current) {
      clearTimeout(throttleRef.current)
      throttleRef.current = null
    }
    setState(valueRef.current)
  }, [])

  // Get current value without triggering re-render
  const getCurrentValue = useCallback(() => valueRef.current, [])

  return {
    state,
    setState: throttledSetState,
    forceUpdate,
    getCurrentValue,
    valueRef,
  }
}
